// One plan, and the four places you can be inside it.
//
// The private chat, the conversation, the decisions, the people. Pending actions are a
// fifth surface and have no tab, because they render inline in the group conversation
// where they were raised.
//
// One `usePlan` for all four, so switching tabs is not a refetch and a signal that arrives
// while the decisions are on screen updates the conversation behind it. The whole plan is
// read at once because the whole plan is small and because five reads that can disagree
// with each other is five reads that will. The private thread is the one piece read
// outside that pipeline — see `usePrivateThread` — because it is a second thread, not a
// second plan.
//
// The refusal path is worth reading. A 404 from this API means: no such plan, or not
// yours, or not a participant, or a malformed id — deliberately indistinguishable, because
// telling them apart would confirm that a preparing thread exists. So this screen says the
// one true thing that covers all of them and does not guess which it was.
//
// Only that considered no earns the sentence. A 401 is a stale bearer waiting on the
// vendor's next refresh, not a withheld plan, so it reads as still signing in — `usePlan`
// re-reads when the session store publishes a fresh token or the network comes back.
//
// ---------------------------------------------------------------------------
// THE PAGE BESIDE THE CONVERSATION
// ---------------------------------------------------------------------------
//
// The body is `[page] [conversation]`, with the room switcher at the top of the conversation.
// The address says which page: the plan's home (`/plans/:id`), the family page (`/people`), a
// topic's page (`/topics/:key`, and `/topics/:key/issues`, the same page), a topic's review screen
// (`/topics/:key/review`) or one of its issues (`/topics/:key/issues/:id`, with `/standing` and
// `/history` after it, which count as being in that topic), and inside a topic the room can be
// named by a suffix (`/private`, `/principals`). An issue's pages sit beside the reader's own
// conversation about that issue and nothing else. Nothing about where a
// reader is lives on the server: reload, back and forward work from the address alone, and
// every move between pages is a link.
//
// Below the desktop breakpoint the page is not a column: `DecisionsMobileStrip` (handed to
// whichever of `Conversation`/`PrivateChat` is rendering, so it sits above the composer)
// opens it from a bottom sheet named for the current page.
//
// Outside a topic the conversation is the reader's own plan-level one. Inside a topic it is
// that topic's: the family conversation once it exists, else the principals', else the
// reader's own, through `RoomSwitcher`'s row at the top of the conversation.

import {
  Fragment,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { IssueEntryRef } from '../api/contract.js';
import { AccountMenu } from '../components/AccountMenu.js';

// Vite removes this import and its chunk from builds where the operator flag is absent.
const DebugMenu =
  import.meta.env.VITE_MOR_OPERATOR_ENABLED === 'true'
    ? lazy(() =>
        import('../operator/DebugMenu.js').then((module) => ({
          default: module.DebugMenu,
        })),
      )
    : null;
import { Wordmark } from '../components/Wordmark.js';
import { DecisionsMobileStrip } from '../components/DecisionsMobileStrip.js';
import { DecisionsPanel } from '../components/DecisionsPanel.js';
import { FilePage } from '../components/FilePage.js';
import { FilesPage } from '../components/FilesPage.js';
import { FamilyPage } from '../components/FamilyPage.js';
import { DiscoveryPage } from '../components/DiscoveryPage.js';
import { CatalogPage } from '../components/CatalogPage.js';
import { HomePage } from '../components/HomePage.js';
import { TopicListPage } from '../components/TopicListPage.js';
import { TopicReviewPage } from '../components/TopicReviewPage.js';
import { DemoWelcome } from '../components/DemoWelcome.js';
import { SplitHandle } from '../components/SplitHandle.js';
import { RoomSwitcher } from '../components/RoomSwitcher.js';
import { useDemoSeatIntro } from '../demo/useDemoSeatIntro.js';
import { principalsRoomLabel } from '../format.js';
import { defaultInternals, type Internals } from '../internals.js';
import type { PlanSnapshot } from '../plan/snapshot.js';
import {
  groupThreadOf,
  issueThreadOf,
  ownThreadOf,
  viewerIsPrincipal,
  principalsThreadOf,
  type ThreadChoice,
} from '../plan/snapshot.js';
import { useIssue } from '../plan/useIssue.js';
import { useStartedIssue } from '../plan/useStartedIssue.js';
import { usePlan } from '../plan/usePlan.js';
import { useStartedTopic } from '../plan/useStartedTopic.js';
import { VoiceContext } from '../voice/VoiceContext.js';
import { useVoice } from '../voice/useVoice.js';
import { Conversation } from './Conversation.js';
import { IssueHistoryPage } from './IssueHistoryPage.js';
import { IssuePage } from './IssuePage.js';
import { IssueStandingPage } from './IssueStandingPage.js';
import { PersonPage } from './PersonPage.js';
import { PrivateChat } from './PrivateChat.js';

export type PlanScreenProps = {
  client: ApiClient;
  internals?: Internals;
  live?: boolean;
  pollMs?: number;
};

/** Where in the plan the address says the reader is. */
type Where =
  | { readonly screen: 'home' }
  | { readonly screen: 'start' }
  | { readonly screen: 'people' }
  | { readonly screen: 'person'; readonly id: string }
  | { readonly screen: 'catalog' }
  | { readonly screen: 'files' }
  | { readonly screen: 'file'; readonly id: string }
  | {
      readonly screen: 'topic';
      readonly key: string;
      readonly review: boolean;
      readonly room: 'private' | 'principals' | null;
    }
  | {
      readonly screen: 'issue';
      readonly key: string;
      readonly id: string;
      readonly page: 'workspace' | 'standing' | 'history';
    };

/** The part of the address after `/plans/:planId`, or null when it names nothing. */
function parseWhere(rest: string): Where | null {
  const segments = rest.split('/').filter((segment) => segment !== '');
  if (segments.length === 0) {
    return { screen: 'home' };
  }
  if (segments.length === 1 && segments[0] === 'start') {
    return { screen: 'start' };
  }
  if (segments.length === 1 && segments[0] === 'people') {
    return { screen: 'people' };
  }
  if (segments.length === 2 && segments[0] === 'people' && segments[1] !== undefined) {
    return { screen: 'person', id: segments[1] };
  }
  if (segments.length === 1 && segments[0] === 'topics') {
    return { screen: 'catalog' };
  }
  if (segments.length === 1 && segments[0] === 'files') {
    return { screen: 'files' };
  }
  if (segments.length === 2 && segments[0] === 'files' && segments[1] !== undefined) {
    return { screen: 'file', id: segments[1] };
  }
  if (segments[0] !== 'topics' || segments[1] === undefined) {
    return null;
  }
  if (segments[2] === 'issues') {
    if (segments.length === 3) {
      return { screen: 'topic', key: segments[1], review: false, room: null };
    }
    const id = segments[3];
    const page = segments[4];
    if (id === undefined || segments.length > 5) {
      return null;
    }
    if (page === undefined) {
      return { screen: 'issue', key: segments[1], id, page: 'workspace' };
    }
    if (page === 'standing' || page === 'history') {
      return { screen: 'issue', key: segments[1], id, page };
    }
    return null;
  }
  const tail = segments.slice(2);
  const review = tail[0] === 'review';
  const room = review ? tail[1] : tail[0];
  if (tail.length > (review ? 2 : 1)) {
    return null;
  }
  if (room !== undefined && room !== 'private' && room !== 'principals') {
    return null;
  }
  return { screen: 'topic', key: segments[1], review, room: room ?? null };
}

export function PlanScreen({
  client,
  internals,
  live,
  pollMs,
}: PlanScreenProps): JSX.Element {
  const { planId, '*': rest } = useParams<{ planId: string; '*': string }>();
  const chosen = internals ?? defaultInternals;
  const where = parseWhere(rest ?? '');
  const inTopic = where?.screen === 'topic' || where?.screen === 'issue';
  const topicKey = inTopic ? where.key : null;
  const issueId = where?.screen === 'issue' ? where.id : null;
  const choice: ThreadChoice = {
    topic: topicKey,
    room: where?.screen === 'topic' ? where.room : null,
    issue: issueId,
  };
  const state = usePlan({
    client,
    planId: planId ?? '',
    internals: chosen,
    choice,
    ...(live === undefined ? {} : { live }),
    ...(pollMs === undefined ? {} : { pollMs }),
  });
  // One voice session for the whole screen; the conversations hand it their thread.
  const voice = useVoice(client, planId ?? '');
  // Hooked here, unconditionally, rather than after the status checks below — `PlanScreen`
  // returns early for several statuses, and a hook cannot follow a conditional return.
  // `readDemoSource` for a plan that turns out not to be a demo (or has not loaded yet)
  // costs nothing: the hook itself no-ops until `isDemo` is true.
  const demoSeat = useDemoSeatIntro(
    client,
    planId ?? '',
    state.snapshot?.plan.isDemo ?? false,
  );

  // The issue an issue page is about, read beside the snapshot.
  const issue = useIssue(client, planId ?? '', issueId, state.snapshot);

  // A file page's crumb names the file; the page reads the file itself, so this reads it
  // once more for the bar.
  const fileId = where?.screen === 'file' ? where.id : null;
  const [fileName, setFileName] = useState<string | null>(null);
  useEffect(() => {
    setFileName(null);
    if (fileId === null) {
      return;
    }
    let current = true;
    client.getFile(planId ?? '', fileId).then(
      (loaded) => {
        if (current) {
          setFileName(loaded.name);
        }
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [client, planId, fileId]);

  // The first visit to an issue the reader has no conversation about yet, for example one they
  // added by hand, opens it for them. Once per issue per mount; a failed open may be retried.
  const openedIssues = useRef(new Set<string>());
  // Only a principal writes an issue's sections, so only a principal gets a conversation.
  const issuePrincipal = state.snapshot !== null && viewerIsPrincipal(state.snapshot);
  const noIssueThread =
    issueId !== null &&
    issuePrincipal &&
    state.snapshot !== null &&
    issueThreadOf(state.snapshot.threads, issueId) === null;
  useEffect(() => {
    if (!noIssueThread || issueId === null || openedIssues.current.has(issueId)) {
      return;
    }
    openedIssues.current.add(issueId);
    const reread = (): void => {
      state.refetch();
    };
    const failedOpen = (): void => {
      if (issueId !== null) {
        openedIssues.current.delete(issueId);
      }
      state.refetch();
    };
    void client.openIssue(planId ?? '', issueId).then(reread, failedOpen);
    // `state.refetch` is stable between snapshots; only the missing thread drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noIssueThread, issueId, planId, client]);

  // The first visit to a topic the reader has been invited into opens it for them: that is
  // what posts the topic's opening into their conversation. Once per topic per mount.
  const opened = useRef(new Set<string>());
  const refetchPlan = state.refetch;
  const visiting = state.snapshot?.topics.find((topic) => topic.key === topicKey);
  const needsOpening =
    visiting !== undefined && visiting.viewer.invited && !visiting.viewer.started;
  useEffect(() => {
    if (!needsOpening || topicKey === null || opened.current.has(topicKey)) {
      return;
    }
    opened.current.add(topicKey);
    const refetch = (): void => refetchPlan();
    void client.openTopic(planId ?? '', topicKey).then(refetch, refetch);
  }, [needsOpening, topicKey, planId, client, refetchPlan]);

  // Mor can open a topic from the plan-level conversation, and the screen follows the snapshot
  // rather than a stored page: a topic that becomes started while the reader is on the home
  // page takes them to it, as the Start button does.
  const navigate = useNavigate();
  // When the screen moves the reader on its own, the reply box takes focus once the new page
  // has rendered, if it had focus.
  const refocusComposer = useRef(false);
  const followTo = (to: string): void => {
    refocusComposer.current =
      document.activeElement instanceof HTMLElement &&
      document.activeElement.classList.contains('composer__field');
    navigate(to);
  };
  const addressKey = `${where?.screen ?? ''}:${topicKey ?? ''}`;
  useEffect(() => {
    if (!refocusComposer.current) {
      return;
    }
    let tries = 0;
    let frame = 0;
    const attempt = (): void => {
      const field = document.querySelector<HTMLTextAreaElement>('.composer__field');
      if (field !== null) {
        field.focus();
        refocusComposer.current = false;
      } else if (tries < 20) {
        tries += 1;
        frame = requestAnimationFrame(attempt);
      } else {
        refocusComposer.current = false;
      }
    };
    attempt();
    return () => cancelAnimationFrame(frame);
  }, [addressKey]);
  // Mor opens an issue from the topic conversation, and the screen follows into its workspace.
  useStartedIssue(state.snapshot, (id, key) => {
    if (where?.screen === 'topic' && key !== null && key === where.key) {
      followTo(`/plans/${planId ?? ''}/topics/${key}/issues/${id}`);
    }
  });
  const onPlanPage =
    where?.screen === 'home' || where?.screen === 'start' || where?.screen === 'people';
  useStartedTopic(state.snapshot, (key) => {
    if (onPlanPage) {
      followTo(`/plans/${planId ?? ''}/topics/${key}`);
    }
  });
  const base = `/plans/${planId ?? ''}`;
  if (where === null) {
    return <Navigate to={base} replace />;
  }

  if (state.status === 'loading') {
    return (
      <PlanFrame client={client} snapshot={null} planId={planId ?? ''}>
        <main className="screen screen--plan">
          <p className="screen__loading">Opening…</p>
        </main>
      </PlanFrame>
    );
  }

  if (state.status === 'waiting') {
    return (
      <PlanFrame client={client} snapshot={null} planId={planId ?? ''}>
        <main className="screen screen--plan">
          <p className="screen__loading">Signing you back in…</p>
        </main>
      </PlanFrame>
    );
  }

  if (state.status === 'faltered') {
    return (
      <PlanFrame client={client} snapshot={null} planId={planId ?? ''}>
        <main className="screen screen--plan">
          <p className="screen__problem">That didn’t load. It will keep trying.</p>
        </main>
      </PlanFrame>
    );
  }

  if (state.status === 'refused') {
    return (
      <PlanFrame client={client} snapshot={null} planId={planId ?? ''}>
        <main className="screen screen--plan">
          <p className="screen__problem">This plan isn’t available to you.</p>
        </main>
      </PlanFrame>
    );
  }

  if (state.status === 'unreachable') {
    return (
      <PlanFrame client={client} snapshot={null} planId={planId ?? ''}>
        <main className="screen screen--plan">
          <p className="screen__problem">
            Couldn’t reach the server. Try again in a moment.
          </p>
        </main>
      </PlanFrame>
    );
  }

  const snapshot = state.snapshot;
  const threads = snapshot === null ? [] : snapshot.threads;
  // The first conversation is the creator's alone: anyone whose own plan-level
  // conversation is not a `preparing` one is sent to the home page.
  if (
    snapshot !== null &&
    where.screen === 'start' &&
    ownThreadOf(threads, null)?.kind !== 'preparing'
  ) {
    return <Navigate to={base} replace />;
  }
  // Outside a topic there is only the reader's own plan-level conversation.
  const ownThread = ownThreadOf(threads, topicKey);
  const principalsThread =
    topicKey === null ? null : principalsThreadOf(threads, topicKey);
  const groupThread = topicKey === null ? null : groupThreadOf(threads, topicKey);
  const people = snapshot === null ? [] : snapshot.people;
  // Named from the principals' thread's own participants; see `format.ts`.
  const principalsLabel = principalsRoomLabel(principalsThread, people);

  const topic = snapshot?.topics.find((candidate) => candidate.key === topicKey);
  const topicUrl = `${base}/topics/${topicKey ?? ''}`;
  const onReview = where.screen === 'topic' && where.review;
  const roomBase = onReview ? `${topicUrl}/review` : topicUrl;
  // An issue's pages sit beside the reader's own conversation about it, and no other room.
  const onIssue = where.screen === 'issue';
  // An issue that was removed, or that the viewer may not see, has no conversation to show.
  const issueGone =
    onIssue && (issue.status === 'missing' || issue.view?.removed === true);
  const issueThread = where.screen === 'issue' ? issueThreadOf(threads, where.id) : null;
  const topicLabel = topic?.label ?? null;

  // Inside a topic the address may name a room; one that does not exist falls back to the
  // default order, the family conversation first. The thread itself is the fact; inferring
  // this from who is in the topic would choose a room the caller may not have.
  const room = where.screen === 'topic' ? where.room : null;
  const landing: 'private' | 'principals' | 'conversation' =
    where.screen !== 'topic'
      ? 'private'
      : room === 'private' && ownThread !== null
        ? 'private'
        : room === 'principals' && principalsThread !== null
          ? 'principals'
          : groupThread !== null
            ? 'conversation'
            : principalsThread !== null
              ? 'principals'
              : 'private';

  // Same rule as `RoomSwitcher`: with fewer than two conversations it draws nothing.
  const switcherShown =
    !onIssue &&
    [ownThread, principalsThread, groupThread].filter((thread) => thread !== null)
      .length >= 2;

  // The thread backing whichever room is actually showing.
  const roomThread = onIssue
    ? issueThread
    : landing === 'private'
      ? ownThread
      : landing === 'principals'
        ? principalsThread
        : groupThread;

  // Undo on an issue's card is a change like any other, answered with the fresh issue.
  const undoIssueEntry =
    where.screen === 'issue'
      ? async (entry: IssueEntryRef): Promise<void> => {
          await client.changeRecord(planId ?? '', {
            kind: 'undo_issue_entry',
            issue: where.id,
            section: entry.kind,
            entry: entry.id,
          });
          issue.refetch();
          state.refetch();
        }
      : undefined;

  const pageBody =
    snapshot === null ? null : where.screen === 'home' ? (
      <HomePage state={state} client={client} planId={planId ?? ''} />
    ) : where.screen === 'start' ? (
      <DiscoveryPage
        state={state}
        client={client}
        planId={planId ?? ''}
        originThreadId={ownThread?.id ?? null}
      />
    ) : where.screen === 'people' ? (
      <FamilyPage
        state={state}
        internals={chosen}
        client={client}
        planId={planId ?? ''}
      />
    ) : where.screen === 'person' ? (
      <PersonPage
        key={where.id}
        state={state}
        client={client}
        planId={planId ?? ''}
        rosterEntryId={where.id}
      />
    ) : where.screen === 'catalog' ? (
      <CatalogPage state={state} client={client} planId={planId ?? ''} />
    ) : where.screen === 'files' ? (
      <FilesPage state={state} client={client} planId={planId ?? ''} />
    ) : where.screen === 'file' ? (
      <FilePage state={state} client={client} planId={planId ?? ''} fileId={where.id} />
    ) : topic === undefined || !(topic.viewer.invited || topic.canStart) ? (
      <section className="page">
        <p className="screen__problem">This topic isn’t available to you.</p>
      </section>
    ) : where.screen === 'issue' ? (
      where.page === 'standing' ? (
        <IssueStandingPage
          key={where.id}
          state={state}
          client={client}
          planId={planId ?? ''}
          topic={topic}
          issueId={where.id}
          issue={issue}
        />
      ) : where.page === 'history' ? (
        <IssueHistoryPage
          key={where.id}
          state={state}
          client={client}
          planId={planId ?? ''}
          topic={topic}
          issueId={where.id}
          issue={issue}
        />
      ) : (
        <IssuePage
          key={where.id}
          state={state}
          client={client}
          planId={planId ?? ''}
          topic={topic}
          issueId={where.id}
          issue={issue}
          originThreadId={issueThread?.id ?? null}
        />
      )
    ) : where.review ? (
      <TopicReviewPage
        state={state}
        internals={chosen}
        client={client}
        planId={planId ?? ''}
        topic={topic}
      />
    ) : (
      <TopicListPage
        state={state}
        internals={chosen}
        client={client}
        planId={planId ?? ''}
        topic={topic}
        originThreadId={roomThread?.id ?? null}
      />
    );
  const shownLabel =
    where.screen === 'home'
      ? 'Topics'
      : where.screen === 'start'
        ? 'Getting to know you'
        : where.screen === 'catalog'
          ? 'Planning topics'
          : where.screen === 'people' || where.screen === 'person'
            ? 'Your family'
            : where.screen === 'files' || where.screen === 'file'
              ? 'Files'
              : onReview
                ? 'Review'
                : where.screen === 'issue'
                  ? (issue.view?.title ?? topicLabel ?? 'Issue')
                  : (topicLabel ?? 'Topic');
  // The bar's trail after the plan's name: every crumb but the last leads back.
  const topicCrumb = topicLabel === null ? [] : [{ label: topicLabel, to: topicUrl }];
  // An issue leads to its topic; the first conversation is reached from the home page.
  const issueTitle =
    issue.view?.title ??
    (where.screen === 'issue'
      ? snapshot?.decisions.find((decision) => decision.id === where.id)?.label
      : undefined) ??
    'Issue';
  const issueCrumb = [
    ...topicCrumb,
    {
      label: issueTitle,
      to: `${topicUrl}/issues/${where.screen === 'issue' ? where.id : ''}`,
    },
  ];
  const crumbs: Crumb[] =
    where.screen === 'start'
      ? [{ label: 'Getting to know you' }]
      : where.screen === 'people'
        ? [{ label: 'Your family' }]
        : where.screen === 'person'
          ? [
              { label: 'Your family', to: `${base}/people` },
              {
                label:
                  snapshot?.people.find((candidate) => candidate.id === where.id)?.name ??
                  '',
              },
            ]
          : where.screen === 'catalog'
            ? [{ label: 'Planning topics' }]
            : where.screen === 'files'
              ? [{ label: 'Files' }]
              : where.screen === 'file'
                ? [{ label: 'Files', to: `${base}/files` }, { label: fileName ?? '' }]
                : where.screen === 'topic'
                  ? topicCrumb.map(({ label }) => ({ label }))
                  : where.screen === 'issue'
                    ? where.page === 'workspace'
                      ? [...topicCrumb, { label: issueTitle }]
                      : [
                          ...issueCrumb,
                          {
                            label:
                              where.page === 'standing'
                                ? 'Where things stand'
                                : 'History',
                          },
                        ]
                    : [];
  // Built once and handed to whichever room is showing — see the header comment above.
  const decisionsStrip =
    snapshot === null ? null : (
      <DecisionsMobileStrip label={shownLabel}>{pageBody}</DecisionsMobileStrip>
    );

  const soloPage =
    where.screen === 'home' ||
    where.screen === 'catalog' ||
    where.screen === 'files' ||
    where.screen === 'file' ||
    (where.screen === 'issue' && where.page === 'standing') ||
    issueGone ||
    where.screen === 'people' ||
    where.screen === 'person';
  const screen = soloPage ? (
    <main className="screen screen--plan screen--solo screen--document">
      {snapshot?.plan.isDemo && demoSeat !== null ? (
        <DemoWelcome planId={planId ?? ''} seat={demoSeat} />
      ) : null}
      <div className="planSolo">{pageBody}</div>
    </main>
  ) : (
    <main className="screen screen--plan">
      {snapshot?.plan.isDemo && demoSeat !== null ? (
        <DemoWelcome planId={planId ?? ''} seat={demoSeat} />
      ) : null}
      <div className="planBody">
        {snapshot === null ? null : <div className="planBody__page">{pageBody}</div>}
        {snapshot === null ? null : <SplitHandle />}

        <VoiceContext.Provider value={voice}>
          <div className="planBody__main">
            {onIssue ? null : (
              <RoomSwitcher
                base={roomBase}
                landing={landing}
                ownThread={ownThread}
                principalsThread={principalsThread}
                groupThread={groupThread}
                principalsRoomLabel={principalsLabel}
              />
            )}

            {landing === 'private' && !(onIssue && !issuePrincipal) ? (
              <PrivateChat
                client={client}
                thread={onIssue ? issueThread : ownThread}
                state={state}
                internals={chosen}
                decisionsStrip={decisionsStrip}
                switcherShown={switcherShown}
                {...(undoIssueEntry === undefined
                  ? {}
                  : { issue: { view: issue.view, undo: undoIssueEntry } })}
              />
            ) : null}
            {landing === 'principals' ? (
              <PrivateChat
                client={client}
                thread={principalsThread}
                state={state}
                internals={chosen}
                // "Principals" is internal terminology and never appears as rendered copy,
                // aria-label included — see `RoomSwitcher.tsx`'s header comment and
                // acceptance criterion 2 in `docs/archived/13-visual-refresh.md`.
                label={`Conversation with ${principalsLabel}`}
                emptyText={`Nothing has been said with ${principalsLabel} yet.`}
                roomKind="principals"
                decisionsStrip={decisionsStrip}
                switcherShown={switcherShown}
              />
            ) : null}
            {landing === 'conversation' ? (
              <Conversation
                client={client}
                state={state}
                internals={chosen}
                decisionsStrip={decisionsStrip}
                switcherShown={switcherShown}
              />
            ) : null}
          </div>
        </VoiceContext.Provider>
      </div>
    </main>
  );

  return (
    <PlanFrame client={client} snapshot={snapshot} planId={planId ?? ''} crumbs={crumbs}>
      <div className="planPage">
        <div className="planLayout">
          {screen}
          {snapshot === null ? null : (
            <DecisionsPanel
              client={client}
              planId={planId ?? ''}
              isDemo={snapshot.plan.isDemo}
              demoSeat={demoSeat}
            />
          )}
        </div>
      </div>
    </PlanFrame>
  );
}

type Crumb = { readonly label: string; readonly to?: string };

type PlanFrameProps = {
  client: ApiClient;
  snapshot: PlanSnapshot | null;
  planId: string;
  /** The trail after the plan's name; every crumb but the last is a way back. */
  crumbs?: readonly Crumb[];
  children: ReactNode;
};

/** Header bar, the body that fills the rest of the window, and a plain footer bar. */
function PlanFrame({
  client,
  snapshot,
  planId,
  crumbs = [],
  children,
}: PlanFrameProps): JSX.Element {
  return (
    <div className="planFrame">
      <header className="planBar">
        <div className="planBar__left">
          <Link to="/" className="planBar__brand" aria-label="Mor, your plans">
            <Wordmark className="header__wordmark" />
          </Link>
          <span className="planBar__divider" aria-hidden="true" />
          {/* The mark sits with the NAME: read together they say "The blended family, Demo".
              The name is the way to the plan's home page. */}
          <h1 className="planBar__name" title={snapshot?.plan.name}>
            {crumbs.length === 0 ? (
              <span className="planBar__nameText">{snapshot?.plan.name ?? ''}</span>
            ) : (
              <Link className="planBar__nameLink" to={`/plans/${planId}`}>
                <span className="planBar__nameText">{snapshot?.plan.name ?? ''}</span>
              </Link>
            )}
            {snapshot?.plan.isDemo ? <span className="demoMark">Demo</span> : null}
          </h1>
          {crumbs.map((crumb, index) => (
            <Fragment key={index}>
              <span className="planBar__topicSep" aria-hidden="true">
                ›
              </span>
              {crumb.to === undefined || index === crumbs.length - 1 ? (
                <span className="planBar__topic">{crumb.label}</span>
              ) : (
                <Link className="planBar__topic" to={crumb.to}>
                  {crumb.label}
                </Link>
              )}
            </Fragment>
          ))}
        </div>
        <div className="planBar__right">
          {DebugMenu === null ? null : (
            <Suspense fallback={null}>
              <DebugMenu client={client} planId={planId} />
            </Suspense>
          )}
          <AccountMenu client={client} />
        </div>
      </header>
      {children}
      <footer className="planFoot" />
    </div>
  );
}
